import { Col, DatePicker, Form, Input, InputNumber, Row } from "antd";
import dayjs from "dayjs";
import MultiSelectTag from "../../../components/multi-select-tag";
import { matchedFlagOptions, matchedTeamOptions } from "../../../constants/ap-workflow";
import { DATE_FORMAT, ESPTypeOptions } from "../../../constants/common";
import { IMasterDataType } from "../../../interfaces/master-data";
import { ISuppliersByNameResult } from "../../../interfaces/supplier";
import { listStatusInvoice } from "../../../utils/helper";

interface FilterReportProps {
  dropdownsData: any;
  displayedColumnFilters: string[];
}

const FILTER_NAME = {
  bsc: [
    "bscExportDateFrom",
    "bscExportDateTo",
    "bscSupplierCodes",
    "bscSupplierName",
    "bscCostAmountFrom",
    "bscCostAmountTo",
    "bscGbpCostFrom",
    "bscGbpCostTo",
    "bscParentBookingRefs",
    "bscStatuses",
    "bscCurrencies",
  ],
  esp: [
    "espExportDateFrom",
    "espExportDateTo",
    "espBookingDateFrom",
    "espBookingDateTo",
    "espDepartureDateFrom",
    "espDepartureDateTo",
    "espDueDateFrom",
    "espDueDateTo",
    "espSupplierCodes",
    "espSupplierName",
    "espAmountFrom",
    "espAmountTo",
    "espGbpCostFrom",
    "espGbpCostTo",
    "espStatuses",
    "espTourName",
    "espCurrencies",
    "espTypes",
    "espExportedDatetimeFrom",
    "espExportedDatetimeTo",
  ],
  invoice: ["irReceivedAtFrom", "irReceivedAtTo", "irSupplierCodes", "irSupplierName", "irStatuses"],
  sir: ["sirSupplierCodes", "sirSupplierName", "sirStatuses", "sirCurrencies"],
  sirf: [
    "sirfTransactionDateFrom",
    "sirfTransactionDateTo",
    "sirfSupplierCodes",
    "sirfSupplierName",
    "sirfAmountFrom",
    "sirfAmountTo",
    "sirfCurrencies",
  ],
  apw: ["apwApOwners", "apwFlags", "apwTeams", "apwComment"],
  fw: ["fwApOwners", "fwFlags", "fwTeams", "fwComment"],
  otw: ["otwApOwners", "otwFlags", "otwTeams", "otwComment"],
  tw: ["twApOwners", "twTeams", "twComment"],
};

const FilterReport: React.FC<FilterReportProps> = ({ dropdownsData, displayedColumnFilters }) => {
  const {
    currencies = [],
    suppliers = [],
    apOwners = [],
    sirStatuses = [],
    bscStatuses = [],
    espStatuses = [],
  } = dropdownsData || {};

  const showFilter = (name: string) => displayedColumnFilters.includes(name);

  // Helper for rendering MultiSelectTag
  const renderMultiSelect = (label: string, name: string, options: any[], tagDisplay?: "value" | "label") => (
    <Col span={24}>
      <Form.Item label={label} name={name}>
        <MultiSelectTag allowClear showSearch placeholder="All" options={options} tagDisplay={tagDisplay} />
      </Form.Item>
    </Col>
  );

  const renderInput = (label: string, name: string, placeholder?: string) => (
    <Col span={24}>
      <Form.Item label={label} name={name}>
        <Input placeholder={placeholder} />
      </Form.Item>
    </Col>
  );

  // Helper for rendering InputNumber with dynamic min/max
  const renderInputNumber = (label: string, name: string, minName?: string, maxName?: string, placeholder?: string) => (
    <Form.Item shouldUpdate noStyle>
      {({ getFieldValue }) => (
        <Col span={12}>
          <Form.Item label={label} name={name}>
            <InputNumber
              min={minName ? getFieldValue(minName) || 1 : undefined}
              max={maxName ? getFieldValue(maxName) : undefined}
              style={{ width: "100%" }}
              placeholder={placeholder || label}
            />
          </Form.Item>
        </Col>
      )}
    </Form.Item>
  );

  // Helper for rendering DatePicker with dynamic min/max
  const renderDatePicker = (label: string, name: string, minName?: string, maxName?: string, placeholder?: string) => (
    <Form.Item shouldUpdate noStyle>
      {({ getFieldValue }) => (
        <Col span={12}>
          <Form.Item label={label} name={name}>
            <DatePicker
              format={DATE_FORMAT}
              minDate={minName ? dayjs(getFieldValue(minName), DATE_FORMAT) : undefined}
              maxDate={maxName ? dayjs(getFieldValue(maxName), DATE_FORMAT) : undefined}
              style={{ width: "100%" }}
              placeholder={placeholder || label}
            />
          </Form.Item>
        </Col>
      )}
    </Form.Item>
  );

  // BSC Filter
  const genBSCFilter = () => {
    if (!FILTER_NAME.bsc.some(showFilter)) return null;
    return (
      <>
        <h3 style={{ marginTop: 0, paddingLeft: 8 }}>BSC</h3>
        <Col span={24}>
          <Row gutter={16}>
            {showFilter("bscExportDateFrom") &&
              renderDatePicker("Import Date From", "bscExportDateFrom", undefined, "bscExportDateTo", "From")}
            {showFilter("bscExportDateTo") &&
              renderDatePicker("Import Date To", "bscExportDateTo", "bscExportDateFrom", undefined, "To")}
          </Row>
        </Col>
        {showFilter("bscSupplierCodes") &&
          renderMultiSelect(
            "Supplier Code",
            "bscSupplierCodes",
            suppliers.map((item: ISuppliersByNameResult) => ({
              value: item.code,
              label: `${item.code} - ${item.label}`,
            })),
            "value"
          )}
        {showFilter("bscSupplierName") && renderInput("Supplier Name", "bscSupplierName")}
        {showFilter("bscStatuses") &&
          renderMultiSelect(
            "Status",
            "bscStatuses",
            bscStatuses.map((item: string) => ({
              value: item.toString(),
              label: item,
            }))
          )}
        {showFilter("bscCurrencies") &&
          renderMultiSelect(
            "Currency",
            "bscCurrencies",
            currencies.map((item: IMasterDataType) => ({
              value: item.code,
              label: `${item.code} - ${item.label}`,
            }))
          )}
        <Col span={24}>
          <Row gutter={16}>
            {showFilter("bscCostAmountFrom") &&
              renderInputNumber("From Local Currency Cost", "bscCostAmountFrom", undefined, "bscCostAmountTo", "From")}
            {showFilter("bscCostAmountTo") &&
              renderInputNumber("To Local Currency Cost", "bscCostAmountTo", "bscCostAmountFrom", undefined, "To")}
          </Row>
        </Col>
        <Col span={24}>
          <Row gutter={16}>
            {showFilter("bscGbpCostFrom") &&
              renderInputNumber("From GPB Cost", "bscGbpCostFrom", undefined, "bscGbpCostTo", "From")}
            {showFilter("bscGbpCostTo") &&
              renderInputNumber("To GPB Cost", "bscGbpCostTo", "bscGbpCostFrom", undefined, "To")}
          </Row>
        </Col>
        {showFilter("bscParentBookingRef") && renderInput("Parent Booking Ref", "bscParentBookingRef")}
      </>
    );
  };

  // ESP Filter
  const genESPFilter = () => {
    if (!FILTER_NAME.esp.some(showFilter)) return null;
    return (
      <>
        <h3 style={{ marginTop: 0, paddingLeft: 8 }}>ESP</h3>
        <Col span={24}>
          <Row gutter={16}>
            {showFilter("espExportDateFrom") &&
              renderDatePicker("Import Date From", "espExportDateFrom", undefined, "espExportDateTo", "From")}
            {showFilter("espExportDateTo") &&
              renderDatePicker("Import Date To", "espExportDateTo", "espExportDateFrom", undefined, "To")}
          </Row>
        </Col>
        <Col span={24}>
          <Row gutter={16}>
            {showFilter("espExportedDatetimeFrom") &&
              renderDatePicker("Export Date From", "espExportedDatetimeFrom", undefined, "espExportedDatetimeTo", "From")}
            {showFilter("espExportedDatetimeTo") &&
              renderDatePicker("Export Date To", "espExportedDatetimeTo", "espExportedDatetimeFrom", undefined, "To")}
          </Row>
        </Col>
        <Col span={24}>
          <Row gutter={16}>
            {showFilter("espBookingDateFrom") &&
              renderDatePicker("Booking Date From", "espBookingDateFrom", undefined, "espBookingDateTo", "From")}
            {showFilter("espBookingDateTo") &&
              renderDatePicker("Booking Date To", "espBookingDateTo", "espBookingDateFrom", undefined, "To")}
          </Row>
        </Col>
        <Col span={24}>
          <Row gutter={16}>
            {showFilter("espDepartureDateFrom") &&
              renderDatePicker("Departure Date From", "espDepartureDateFrom", undefined, "espDepartureDateTo", "From")}
            {showFilter("espDepartureDateTo") &&
              renderDatePicker("Departure Date To", "espDepartureDateTo", "espDepartureDateFrom", undefined, "To")}
          </Row>
        </Col>
        <Col span={24}>
          <Row gutter={16}>
            {showFilter("espDueDateFrom") &&
              renderDatePicker("Due Date From", "espDueDateFrom", undefined, "espDueDateTo", "From")}
            {showFilter("espDueDateTo") &&
              renderDatePicker("Due Date To", "espDueDateTo", "espDueDateFrom", undefined, "To")}
          </Row>
        </Col>
        {showFilter("espSupplierCodes") &&
          renderMultiSelect(
            "Supplier Code",
            "espSupplierCodes",
            suppliers.map((item: ISuppliersByNameResult) => ({
              value: item.code,
              label: `${item.code} - ${item.label}`,
            })),
            "value"
          )}
        {showFilter("espSupplierName") && renderInput("Supplier Name", "espSupplierName")}
        <Col span={24}>
          <Row gutter={16}>
            {showFilter("espAmountFrom") &&
              renderInputNumber("From Local Currency Cost", "espAmountFrom", undefined, "espAmountTo", "From")}
            {showFilter("espAmountTo") &&
              renderInputNumber("To Local Currency Cost", "espAmountTo", "espAmountFrom", undefined, "To")}
          </Row>
        </Col>
        <Col span={24}>
          <Row gutter={16}>
            {showFilter("espGbpCostFrom") &&
              renderInputNumber("From GPB Cost", "espGbpCostFrom", undefined, "espGbpCostTo", "From")}
            {showFilter("espGbpCostTo") &&
              renderInputNumber("To GPB Cost", "espGbpCostTo", "espGbpCostFrom", undefined, "To")}
          </Row>
        </Col>
        {showFilter("espStatuses") &&
          renderMultiSelect(
            "Status",
            "espStatuses",
            espStatuses.map((item: string) => ({
              value: item.toString(),
              label: item,
            }))
          )}
        {showFilter("espTourName") && (
          <Col span={24}>
            <Form.Item label="Tour Name" name="espTourName">
              <Input />
            </Form.Item>
          </Col>
        )}
        {showFilter("espCurrencies") &&
          renderMultiSelect(
            "Currency",
            "espCurrencies",
            currencies.map((item: IMasterDataType) => ({
              value: item.code,
              label: `${item.code} - ${item.label}`,
            }))
          )}
        {showFilter("espTypes") &&
          renderMultiSelect(
            "Workflow",
            "espTypes",
            ESPTypeOptions.map((item: any) => ({
              value: item.value,
              label: item.label,
            }))
          )}
      </>
    );
  };

  // INVOICE Filter
  const genInvoiceFilter = () => {
    if (!FILTER_NAME.invoice.some(showFilter)) return null;
    return (
      <>
        <h3 style={{ marginTop: 0, paddingLeft: 8 }}>INVOICE</h3>
        <Col span={24}>
          <Row gutter={16}>
            {showFilter("irReceivedAtFrom") &&
              renderDatePicker("Received Date From", "irReceivedAtFrom", undefined, "irReceivedAtTo", "From")}
            {showFilter("irReceivedAtTo") &&
              renderDatePicker("Received Date To", "irReceivedAtTo", "irReceivedAtFrom", undefined, "To")}
          </Row>
        </Col>
        {showFilter("irSupplierCodes") &&
          renderMultiSelect(
            "Supplier Code",
            "irSupplierCodes",
            suppliers.map((item: ISuppliersByNameResult) => ({
              value: item.code,
              label: `${item.code} - ${item.label}`,
            })),
            "value"
          )}
        {showFilter("irSupplierName") && renderInput("Supplier Name", "irSupplierName")}
        {showFilter("irStatuses") &&
          renderMultiSelect(
            "Status",
            "irStatuses",
            listStatusInvoice.map((item: string) => ({
              value: item.toString(),
              label: item,
            }))
          )}
      </>
    );
  };

  // SIR Filter
  const genSIRFilter = () => {
    if (!FILTER_NAME.sir.some(showFilter)) return null;
    return (
      <>
        <h3 style={{ marginTop: 0, paddingLeft: 8 }}>SIR</h3>
        {showFilter("sirSupplierCodes") &&
          renderMultiSelect(
            "Supplier Code",
            "sirSupplierCodes",
            suppliers.map((item: ISuppliersByNameResult) => ({
              value: item.code,
              label: `${item.code} - ${item.label}`,
            })),
            "value"
          )}
        {showFilter("sirSupplierName") && renderInput("Supplier Name", "sirSupplierName")}
        {showFilter("sirStatuses") &&
          renderMultiSelect(
            "Status",
            "sirStatuses",
            sirStatuses.map((item: string) => ({
              value: item.toString(),
              label: item,
            }))
          )}
        {showFilter("sirCurrencies") &&
          renderMultiSelect(
            "Currency",
            "sirCurrencies",
            currencies.map((item: IMasterDataType) => ({
              value: item.code,
              label: `${item.code} - ${item.label}`,
            }))
          )}
      </>
    );
  };

  // SIR FLIGHT Filter
  const genSIRFlightFilter = () => {
    if (!FILTER_NAME.sirf.some(showFilter)) return null;
    return (
      <>
        <h3 style={{ marginTop: 0, paddingLeft: 8 }}>SIR - FLIGHT</h3>
        <Col span={24}>
          <Row gutter={16}>
            {showFilter("sirfTransactionDateFrom") &&
              renderDatePicker(
                "Transaction Date From",
                "sirfTransactionDateFrom",
                undefined,
                "sirfTransactionDateTo",
                "From"
              )}
            {showFilter("sirfTransactionDateTo") &&
              renderDatePicker(
                "Transaction Date To",
                "sirfTransactionDateTo",
                "sirfTransactionDateFrom",
                undefined,
                "To"
              )}
          </Row>
        </Col>
        {showFilter("sirfSupplierCodes") &&
          renderMultiSelect(
            "Supplier Code",
            "sirfSupplierCodes",
            suppliers.map((item: ISuppliersByNameResult) => ({
              value: item.code,
              label: `${item.code} - ${item.label}`,
            })),
            "value"
          )}
        {showFilter("sirfSupplierName") && renderInput("Supplier Name", "sirfSupplierName")}
        <Col span={24}>
          <Row gutter={16}>
            {showFilter("sirfAmountFrom") &&
              renderInputNumber("Amount From", "sirfAmountFrom", undefined, "sirfAmountTo", "From")}
            {showFilter("sirfAmountTo") &&
              renderInputNumber("Amount To", "sirfAmountTo", "sirfAmountFrom", undefined, "To")}
          </Row>
        </Col>
        {showFilter("sirfCurrencies") &&
          renderMultiSelect(
            "Currency",
            "sirfCurrencies",
            currencies.map((item: IMasterDataType) => ({
              value: item.code,
              label: `${item.code} - ${item.label}`,
            }))
          )}
      </>
    );
  };

  // AP W-FLOW Filter
  const genAPWWorkflowFilter = () => {
    if (!FILTER_NAME.apw.some(showFilter)) return null;
    return (
      <>
        <h3 style={{ marginTop: 0, paddingLeft: 8 }}>AP W-FLOW</h3>
        {showFilter("apwApOwners") &&
          renderMultiSelect(
            "AP Owner",
            "apwApOwners",
            apOwners.map((item: any) => ({
              value: item.id,
              label: `${item.firstName} ${item.lastName}`,
            }))
          )}
        {showFilter("apwFlags") && renderMultiSelect("Flag", "apwFlags", matchedFlagOptions)}
        {showFilter("apwTeams") && renderMultiSelect("Team", "apwTeams", matchedTeamOptions)}
        {showFilter("apwComment") && renderInput("Comments", "apwComment")}
      </>
    );
  };

  // FLIGHT W-FLOW Filter
  const genFlightWFlowFilter = () => {
    if (!FILTER_NAME.fw.some(showFilter)) return null;
    return (
      <>
        <h3 style={{ marginTop: 0, paddingLeft: 8 }}>FLIGHT W-FLOW</h3>
        {showFilter("fwFlags") && renderMultiSelect("Flag", "fwFlags", matchedFlagOptions)}
        {showFilter("fwApOwners") &&
          renderMultiSelect(
            "AP Owner",
            "fwApOwners",
            apOwners.map((item: any) => ({
              value: item.id,
              label: `${item.firstName} ${item.lastName}`,
            }))
          )}
        {showFilter("fwTeams") && renderMultiSelect("Team", "fwTeams", matchedTeamOptions)}
        {showFilter("fwComment") && renderInput("Comments", "fwComment")}
      </>
    );
  };

  // OTHER W-FLOW Filter
  const genOtherWFlowFilter = () => {
    if (!FILTER_NAME.otw.some(showFilter)) return null;
    return (
      <>
        <h3 style={{ marginTop: 0, paddingLeft: 8 }}>OTHER W-FLOW</h3>
        {showFilter("otwApOwners") &&
          renderMultiSelect(
            "AP Owner",
            "otwApOwners",
            apOwners.map((item: any) => ({
              value: item.id,
              label: `${item.firstName} ${item.lastName}`,
            }))
          )}
        {showFilter("otwFlags") && renderMultiSelect("Flag", "otwFlags", matchedFlagOptions)}
        {showFilter("otwTeams") && renderMultiSelect("Team", "otwTeams", matchedTeamOptions)}
        {showFilter("otwComment") && renderInput("Comments", "otwComment")}
      </>
    );
  };

  // TOUR EVENT W-FLOW Filter
  const genTourEventWFlowFilter = () => {
    if (!FILTER_NAME.tw.some(showFilter)) return null;
    return (
      <>
        <h3 style={{ marginTop: 0, paddingLeft: 8 }}>TOUR EVENT W-FLOW</h3>
        {showFilter("twFlags") && renderMultiSelect("Flag", "twFlags", matchedFlagOptions)}
        {showFilter("twApOwners") &&
          renderMultiSelect(
            "AP Owner",
            "twApOwners",
            apOwners.map((item: any) => ({
              value: item.id,
              label: `${item.firstName} ${item.lastName}`,
            }))
          )}
        {showFilter("twTeams") && renderMultiSelect("Team", "twTeams", matchedTeamOptions)}
        {showFilter("twComment") && renderInput("Comments", "twComment")}
      </>
    );
  };

  return (
    <Row gutter={16}>
      {genInvoiceFilter()}
      {genSIRFilter()}
      {genSIRFlightFilter()}
      {genBSCFilter()}
      {genESPFilter()}
      {genAPWWorkflowFilter()}
      {genTourEventWFlowFilter()}
      {genOtherWFlowFilter()}
      {genFlightWFlowFilter()}
    </Row>
  );
};

export default FilterReport;
